import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { Product } from 'src/app/model/product';
import { User } from 'src/app/model/user';
import { AuthService } from 'src/app/service/auth.service';
import { UtilService } from 'src/app/service/util.service';
import { CheckDesktopService } from './../../service/check-desktop.service';
import { DescriptionImageUpdateEvent } from '../description-image-editor/description-image-editor.component';
import { PhotoReplacement } from '../ai-image-edit-overlay/ai-image-edit-overlay.component';

export interface PhotoUpdateEvent {
  type: 'representation' | 'section' | 'summary' | 'price' | 'description';
  index: number;
  sectionIndex?: number;
  paragraphIndex?: number;
  newUrl: string;
  oldUrl?: string;
  updatedHtml?: string;
}

@Component({
  selector: 'meeting-preview',
  templateUrl: './meeting-preview.component.html',
  styleUrls: ['./meeting-preview.component.scss'],
})
export class MeetingPreviewComponent implements OnInit, OnChanges, OnDestroy {
  quillStyle: any;
  @Input() product: Product;
  @Input() isAdminPage = false;
  @Output() updatePhoto = new EventEmitter<PhotoUpdateEvent>();

  host: User;
  isDesktop = true;
  representationScrollConfigs = {
    pagination: {
      clickable: true,
      type: 'fraction',
    },
    speed: 400,
    autoplay: false,
  };

  constructor(
    private utilService: UtilService,
    private authService: AuthService,
    private cds: CheckDesktopService,
    private bottomSheet: MatBottomSheet,
  ) {}

  ngOnInit(): void {
    this.cds.isDesktop.subscribe((resp) => (this.isDesktop = resp));
    this.authService.getCurrentUser().subscribe((user) => {
      this.host = user;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.product.firstChange) {
      this.quillStyle = this.utilService.getQuillStyle();
    }
  }

  openBm(template) {
    this.bottomSheet.open(template, {
      closeOnNavigation: true,
      panelClass: 'nnb-bottom-sheet',
    });
  }

  closeBm() {
    this.bottomSheet.dismiss();
  }

  setPosition(sectionId: string, productId: number) {
    const id = `section-${sectionId}-${productId}`;
    const yOffset = -80;
    const element = document.getElementById(id);
    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }

  /**
   * 가격 포맷팅
   */
  formatPrice(price: number): string {
    if (!price) return '0';
    return price.toLocaleString();
  }

  /**
   * 진행 시간 포맷팅
   */
  formatDuration(): string {
    if (!this.product) return '시간 미정';

    const days = this.product.runningDays || 0;
    const hours = this.product.runningHours || 0;
    const minutes = this.product.runningMinutes || 0;

    let duration = '';
    if (days > 0) duration += `${days}일 `;
    if (hours > 0) duration += `${hours}시간 `;
    if (minutes > 0) duration += `${minutes}분`;

    return duration.trim() || '시간 미정';
  }

  /**
   * 지역 정보 반환
   */
  getRegion(): string {
    if (!this.product) return '지역';

    // 실제 UtilService의 setRegion 로직과 동일하게 구현
    if (this.product.address) {
      if (this.product.address.includes('서울')) return '서울';
      if (this.product.address.includes('경기')) return '경기';
      if (this.product.address.includes('인천')) return '인천';
      if (this.product.address.includes('부산')) return '부산';
      if (this.product.address.includes('대구')) return '대구';
      if (this.product.address.includes('광주')) return '광주';
      if (this.product.address.includes('대전')) return '대전';
      if (this.product.address.includes('울산')) return '울산';
      if (this.product.address.includes('세종')) return '세종';
      if (this.product.address.includes('강원')) return '강원';
      if (this.product.address.includes('충북')) return '충북';
      if (this.product.address.includes('충남')) return '충남';
      if (this.product.address.includes('전북')) return '전북';
      if (this.product.address.includes('전남')) return '전남';
      if (this.product.address.includes('경북')) return '경북';
      if (this.product.address.includes('경남')) return '경남';
      if (this.product.address.includes('제주')) return '제주';
    }
    return '전국';
  }

  /**
   * 할인율 계산
   */
  getDiscountPercent(): number {
    if (!this.product?.price || !this.product?.discountPrice) return 0;

    return Math.round(((this.product.price - this.product.discountPrice) / this.product.price) * 100);
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  // ============================================
  // AI Image Enhancement Event Handlers
  // ============================================

  /** 실시간 미리보기는 폼이 바뀔 때마다 요약·섹션 객체를 새로 만들므로, 참조로 추적하면 진행 중인 AI 보정 오버레이까지 다시 생성된다. */
  trackByIndex(index: number): number {
    return index;
  }

  getRepresentationKey(index: number): string {
    return `rep-${index}`;
  }

  getSectionKey(sectionIndex: number, paragraphIndex: number, photoIndex: number): string {
    return `section-${sectionIndex}-${paragraphIndex}-${photoIndex}`;
  }

  getSummaryKey(summaryIndex: number): string {
    return `summary-${summaryIndex}`;
  }

  /**
   * 대표 이미지 업데이트 핸들러
   */
  onRepresentationPhotoUpdated(index: number, replacement: PhotoReplacement): void {
    this.updatePhoto.emit({
      type: 'representation',
      index,
      newUrl: replacement.newUrl,
      oldUrl: replacement.previousUrl,
    });
  }

  /**
   * 섹션 이미지 업데이트 핸들러
   */
  onSectionPhotoUpdated(
    sectionIndex: number,
    paragraphIndex: number,
    photoIndex: number,
    replacement: PhotoReplacement,
  ): void {
    this.updatePhoto.emit({
      type: 'section',
      index: photoIndex,
      sectionIndex,
      paragraphIndex,
      newUrl: replacement.newUrl,
      oldUrl: replacement.previousUrl,
    });
  }

  /**
   * 상세요약 이미지 업데이트 핸들러
   */
  onSummaryPhotoUpdated(summaryIndex: number, replacement: PhotoReplacement): void {
    this.updatePhoto.emit({
      type: 'summary',
      index: summaryIndex,
      newUrl: replacement.newUrl,
      oldUrl: replacement.previousUrl,
    });
  }

  /**
   * 가격 이미지 업데이트 핸들러
   */
  onPriceImageUpdated(replacement: PhotoReplacement): void {
    this.updatePhoto.emit({
      type: 'price',
      index: 0,
      newUrl: replacement.newUrl,
      oldUrl: replacement.previousUrl,
    });
  }

  /**
   * Description 이미지 업데이트 핸들러
   */
  onDescriptionImageUpdated(event: DescriptionImageUpdateEvent): void {
    this.updatePhoto.emit({
      type: 'description',
      index: 0,
      newUrl: event.newUrl,
      oldUrl: event.oldUrl,
      updatedHtml: event.updatedHtml,
    });
  }
}
